import { useTicketUpdateActions } from "app/containers/Features/containers/Ticket/components/useTicketUpdateActions";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { ticketGroupsActions } from "../../containers/Tickets/redux/slice";
import { GroupByTypeEnum } from "../../types";
import { TicketTagsActionsEnum } from "../../containers/Tickets/TicketFields/types";
import { actions as ticketFieldActions } from "../../containers/Tickets/TicketFields/redux/slice";
interface DragFieldSyncProps {
  groupBy: GroupByTypeEnum;
  subGroupBy: GroupByTypeEnum;
  sourceGroupId: string;
  targetGroupId: string;
  sourceSubGroupId: string | null;
  targetSubGroupId: string | null;
  ticketIds: string[];
}

// ─── localMap ─────────────────────────────────────────────────────────────────

const localMap: Partial<
  Record<GroupByTypeEnum, (value: string | string[]) => object>
> = {
  [GroupByTypeEnum.STATUS]: (value) => ({ status: value }),
  [GroupByTypeEnum.ASSIGNEE]: (value) => ({ assignedTo: value }),
  [GroupByTypeEnum.PRIORITY]: (value) => ({ priority: value }),
  [GroupByTypeEnum.TICKET_TYPE]: (value) => ({ type: value }),
  [GroupByTypeEnum.DUE_DATE]: (value) => ({ dueDate: value }),
  [GroupByTypeEnum.TAGS]: (value) => ({
    tags: Array.isArray(value) ? value : value.split(",").filter(Boolean),
  }),
};

// ─── useDragFieldSync ─────────────────────────────────────────────────────────

export function useDragFieldSync() {
  const dispatch = useDispatch();
  const { project } = useParams();
  const updates = useTicketUpdateActions({ projectId: project as string });

  // serverMap — GroupByTypeEnum → API function

  const serverMap: Partial<
    Record<GroupByTypeEnum, (value: string | string[], ids: string[]) => void>
  > = {
    [GroupByTypeEnum.STATUS]: (value, ids) =>
      updates.statusUpdate(value as string, ids, () => {}, true),
    [GroupByTypeEnum.ASSIGNEE]: (value, ids) =>
      updates.assigneeUpdate(value as string, ids, () => {}, true),
    [GroupByTypeEnum.PRIORITY]: (value, ids) =>
      updates.priorityUpdate(value as string, ids, () => {}, true),
    [GroupByTypeEnum.TICKET_TYPE]: (value, ids) =>
      updates.ticketTypeUpdate(value as string, ids, () => {}, true),
    [GroupByTypeEnum.DUE_DATE]: (value, ids) =>
      updates.dueDateUpdate(value as string, ids, () => {}, true),

    // [GroupByTypeEnum.SPRINT]:   (value, ids) => updates.sprintUpdate(value, ids),
    // [GroupByTypeEnum.EPIC]:     (value, ids) => updates.epicUpdate(value, ids),
  };

  const syncField = (
    field: GroupByTypeEnum,
    value: string | string[],
    ticketIds: string[],
  ) => {
    const localUpdate = localMap[field]?.(value as string | string[]) ?? {};
    dispatch(
      ticketGroupsActions.updateTicketFieldLocally({
        ticketIds,
        updates: localUpdate,
      }),
    );

    if (field === GroupByTypeEnum.TAGS) {
      const tagIds = Array.isArray(value)
        ? value
        : value.split(",").filter(Boolean);

      dispatch(
        ticketFieldActions.ticketTagsUpdate({
          hideToast: true,
          data: {
            tags: tagIds,
            projectId: project as string,
            tagActions: TicketTagsActionsEnum.Replace,
            ticketIds: ticketIds,
          },
          callback: () => {},
        }),
      );

      return;
    }
    serverMap[field]?.(value, ticketIds);
  };

  return ({
    groupBy,
    subGroupBy,
    sourceGroupId,
    targetGroupId,
    sourceSubGroupId,
    targetSubGroupId,
    ticketIds,
  }: DragFieldSyncProps) => {
    // ── groupBy field sync (e.g. status change hone pe) ──────────────────
    if (
      groupBy &&
      groupBy !== GroupByTypeEnum.NONE &&
      sourceGroupId !== targetGroupId
    ) {
      syncField(groupBy, targetGroupId, ticketIds);
    }

    // ── subGroupBy field sync (e.g. assignee/sprint change hone pe) ──────
    if (
      subGroupBy &&
      subGroupBy !== GroupByTypeEnum.NONE &&
      sourceSubGroupId !== targetSubGroupId
    ) {
      syncField(subGroupBy, targetSubGroupId as string | string[], ticketIds);
    }
  };
}
